import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const SupportUs = () => {
    const { currentUser } = useAuth();
    const [selectedAmount, setSelectedAmount] = useState(null);
    const [customAmount, setCustomAmount] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [processing, setProcessing] = useState(false);

    const donationAmounts = [
        { amount: 50, label: "Basic Supporter", emoji: "🤝" },
        { amount: 100, label: "Bronze Supporter", emoji: "🥉" },
        { amount: 250, label: "Silver Supporter", emoji: "🥈" },
        { amount: 500, label: "Gold Supporter", emoji: "🥇" },
        { amount: 1000, label: "Platinum Supporter", emoji: "💎" }
    ];

    const handleDonate = () => {
        if (!agreed) {
            alert('Please agree to the terms');
            return;
        }
        
        let finalAmount = selectedAmount;
        if (selectedAmount === 'custom' && customAmount) {
            finalAmount = parseFloat(customAmount);
        }
        
        if (!finalAmount || finalAmount < 10) {
            alert('Please select or enter a donation amount (minimum R10)');
            return;
        }
        
        setProcessing(true);
        // Replace with your Yoco payment link for donations
        window.open(`https://pay.yoco.com/gradpath-donate?amount=${finalAmount}`, '_blank');
        setProcessing(false);
    };

    const impactStats = [
        { number: "500+", label: "Students Helped", icon: "fas fa-users" },
        { number: "50+", label: "Internships Placed", icon: "fas fa-briefcase" },
        { number: "30+", label: "Partner Companies", icon: "fas fa-building" },
        { number: "R150K+", label: "Bursaries Funded", icon: "fas fa-hand-holding-heart" }
    ];

    const supporterTiers = [
        {
            tier: "Bronze Supporter",
            amount: "R100+",
            benefits: ["Name on supporters page", "Monthly newsletter", "Social media shoutout"],
            color: "from-amber-600 to-amber-800"
        },
        {
            tier: "Silver Supporter",
            amount: "R250+",
            benefits: ["All Bronze benefits", "Digital certificate", "Invite to annual event", "Priority support"],
            color: "from-gray-400 to-gray-600"
        },
        {
            tier: "Gold Supporter",
            amount: "R500+",
            benefits: ["All Silver benefits", "Custom thank you video", "Logo on website", "One-on-one with founder"],
            color: "from-yellow-500 to-yellow-700"
        },
        {
            tier: "Platinum Supporter",
            amount: "R1000+",
            benefits: ["All Gold benefits", "Named scholarship opportunity", "Annual report feature", "Lifetime recognition"],
            color: "from-cyan-500 to-blue-700"
        }
    ];

    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">
            {/* Header Section */}
            <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-pink-500 to-red-500 rounded-full mb-4 shadow-lg">
                    <i className="fas fa-heart text-white text-4xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gradpath-dark mb-4">
                    Support GradPath
                </h1>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                    Help us bridge the gap between graduates and opportunities. Your donation changes lives.
                </p>
            </div>

            {/* Impact Stats */}
            <div className="grid md:grid-cols-4 gap-6 mb-12">
                {impactStats.map((stat, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                        <div className="w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className={`${stat.icon} text-white text-2xl`}></i>
                        </div>
                        <p className="text-2xl font-bold text-gradpath-dark">{stat.number}</p>
                        <p className="text-gray-500 text-sm">{stat.label}</p>
                    </div>
                ))}
            </div>

            {/* Why Donate Section */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-12">
                <h2 className="text-2xl font-bold text-gradpath-dark mb-6 text-center">Why Your Support Matters</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                        <i className="fas fa-graduation-cap text-4xl text-gradpath-green mb-3"></i>
                        <h3 className="font-bold mb-2">Empower Graduates</h3>
                        <p className="text-gray-600 text-sm">Help graduates access jobs, internships, and career guidance</p>
                    </div>
                    <div className="text-center">
                        <i className="fas fa-chalkboard-user text-4xl text-gradpath-green mb-3"></i>
                        <h3 className="font-bold mb-2">Support Mentorship</h3>
                        <p className="text-gray-600 text-sm">Fund mentorship programmes and career coaching sessions</p>
                    </div>
                    <div className="text-center">
                        <i className="fas fa-hand-holding-heart text-4xl text-gradpath-green mb-3"></i>
                        <h3 className="font-bold mb-2">Create Bursaries</h3>
                        <p className="text-gray-600 text-sm">Provide financial assistance to students in need</p>
                    </div>
                </div>
            </div>

            {/* Donation Amounts */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                <h2 className="text-2xl font-bold text-gradpath-dark mb-6 text-center">Choose Your Donation Amount</h2>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    {donationAmounts.map((item, index) => (
                        <button
                            key={index}
                            onClick={() => {
                                setSelectedAmount(item.amount);
                                setCustomAmount('');
                            }}
                            className={`p-4 rounded-xl text-center transition-all ${
                                selectedAmount === item.amount
                                    ? 'bg-gradient-to-r from-gradpath-green to-gradpath-dark text-white shadow-lg'
                                    : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                            }`}
                        >
                            <div className="text-2xl mb-1">{item.emoji}</div>
                            <div className="font-bold">R{item.amount}</div>
                            <div className="text-xs mt-1">{item.label}</div>
                        </button>
                    ))}
                </div>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Custom Amount (R)</label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                        <input
                            type="number"
                            min="10"
                            placeholder="Enter amount"
                            value={customAmount}
                            onChange={(e) => {
                                setCustomAmount(e.target.value);
                                setSelectedAmount('custom');
                            }}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                        />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Minimum donation: R10</p>
                </div>

                {!currentUser && (
                    <div className="mb-4 p-3 bg-yellow-50 rounded-lg text-center text-sm">
                        <i className="fas fa-info-circle mr-2 text-yellow-600"></i>
                        Please <button onClick={() => document.getElementById('loginBtn')?.click()} className="text-gradpath-green font-semibold hover:underline">login</button> or 
                        <button onClick={() => document.getElementById('signupBtn')?.click()} className="text-gradpath-green font-semibold hover:underline ml-1">sign up</button> to support us
                    </div>
                )}

                <button
                    onClick={handleDonate}
                    disabled={!currentUser || processing}
                    className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-bold py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {processing ? (
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                    ) : (
                        <i className="fas fa-heart mr-2"></i>
                    )}
                    Donate Now
                </button>
            </div>

            {/* Supporter Tiers */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-12">
                <h2 className="text-2xl font-bold text-gradpath-dark mb-6 text-center">Supporter Recognition Tiers</h2>
                <div className="grid md:grid-cols-4 gap-4">
                    {supporterTiers.map((tier, index) => (
                        <div key={index} className="bg-gray-50 rounded-xl p-4 text-center hover:shadow-md transition-all">
                            <div className={`bg-gradient-to-r ${tier.color} text-white rounded-lg p-2 mb-3`}>
                                <p className="font-bold">{tier.tier}</p>
                                <p className="text-sm">{tier.amount}</p>
                            </div>
                            <ul className="text-left text-sm space-y-1">
                                {tier.benefits.map((benefit, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <i className="fas fa-check-circle text-green-500 text-xs mt-0.5"></i>
                                        <span className="text-gray-600">{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            {/* How Funds Are Used */}
            <div className="bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-xl p-8 text-white mb-12">
                <h2 className="text-2xl font-bold mb-6 text-center">How Your Donation Helps</h2>
                <div className="grid md:grid-cols-4 gap-4 text-center">
                    <div>
                        <i className="fas fa-laptop-code text-3xl mb-2"></i>
                        <p className="font-semibold">Platform Maintenance</p>
                        <p className="text-sm text-white/80">Keep our job portal running</p>
                    </div>
                    <div>
                        <i className="fas fa-chalkboard-user text-3xl mb-2"></i>
                        <p className="font-semibold">Career Workshops</p>
                        <p className="text-sm text-white/80">Free training sessions</p>
                    </div>
                    <div>
                        <i className="fas fa-hand-holding-heart text-3xl mb-2"></i>
                        <p className="font-semibold">Bursaries</p>
                        <p className="text-sm text-white/80">Financial aid for students</p>
                    </div>
                    <div>
                        <i className="fas fa-bullhorn text-3xl mb-2"></i>
                        <p className="font-semibold">Outreach</p>
                        <p className="text-sm text-white/80">Reach more graduates</p>
                    </div>
                </div>
            </div>

            {/* Disclaimers */}
            <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-gray-400">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="fas fa-info-circle text-gray-500"></i>
                    Important Information
                </h3>
                
                <div className="space-y-2 text-sm text-gray-600">
                    <p><strong>Tax Deductible:</strong> GradPath is a registered non-profit organization. Donations are tax-deductible in South Africa. You will receive a Section 18A tax certificate for donations over R100.</p>
                    <p><strong>Receipt:</strong> An email receipt will be sent to your registered email address.</p>
                    <p><strong>Privacy:</strong> Your personal information will be kept confidential. We will only use your name for recognition if you opt in.</p>
                    <p><strong>Refund Policy:</strong> Donations are non-refundable. Please contact us if you have any concerns about your donation.</p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.value)}
                            className="mt-0.5 w-4 h-4 text-gradpath-green rounded focus:ring-gradpath-green"
                        />
                        <span className="text-sm text-gray-700">
                            I understand that my donation will be used to support GradPath's mission of helping graduates find opportunities. 
                            I acknowledge that donations are non-refundable.
                        </span>
                    </label>
                </div>
            </div>

            {/* Contact for Corporate Sponsors */}
            <div className="bg-white rounded-xl shadow-md p-6 mt-8 text-center">
                <i className="fas fa-building text-4xl text-gradpath-green mb-3"></i>
                <h3 className="text-xl font-bold text-gradpath-dark mb-2">Corporate Sponsorship</h3>
                <p className="text-gray-600 mb-4">Interested in becoming a corporate sponsor? Contact us for customized partnership packages.</p>
                <Link to="/contact" className="text-gradpath-green font-semibold hover:underline">
                    Contact Our Partnerships Team →
                </Link>
            </div>
        </div>
    );
};

export default SupportUs;